import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaUser,
  FaPhone,
  FaPaperPlane,
  FaCheck,
  FaListUl,
} from "react-icons/fa";
import "./contact-form.scss";

/* === Your Google Apps Script Web App URL === */
const GS_URL =
  "https://script.google.com/macros/s/AKfycbxFq83Xam2jWqAF9xwgt1hs5J9fPmIpyt8yejGZDqrifSXp_EdBB3vY4cJZhiJoGIrSOg/exec";

/* === Helper functions === */
function formatUzPhoneDisplay(value) {
  const d = value.replace(/\D/g, "");
  let rest = d.startsWith("998") ? d.slice(3) : d;
  if (rest.length > 9) rest = rest.slice(0, 9);
  const a = rest.slice(0, 2);
  const b = rest.slice(2, 5);
  const c = rest.slice(5, 7);
  const e = rest.slice(7, 9);
  if (!rest) return "+998 ";
  if (rest.length <= 2) return `+998 (${a}`;
  if (rest.length <= 5) return `+998 (${a}) ${b}`;
  if (rest.length <= 7) return `+998 (${a}) ${b}-${c}`;
  return `+998 (${a}) ${b}-${c}-${e}`;
}

function normalizeToE164(display) {
  const digits = display.replace(/\D/g, "");
  let rest = digits;
  if (rest.startsWith("998")) rest = rest.slice(3);
  if (rest.length > 9) rest = rest.slice(0, 9);
  return `+998${rest}`;
}

/* === Function that sends data to Google Sheets === */
async function sendLead({ name, phone, program }) {
  const body = new URLSearchParams({
    name,
    phone,
    program: program || "Не выбрано",
    source: "site-modal",
  }).toString();

  try {
    const response = await fetch(GS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });

    const text = await response.text();
    console.log("Server response:", text);

    if (text.startsWith("{")) {
      const json = JSON.parse(text);
      if (json.ok) return json;
    }
    throw new Error("Invalid response");
  } catch (err) {
    console.error("Send error:", err);
    throw new Error("submit_failed");
  }
}

/* === Main React Component === */
const ContactForm = ({ isOpen, onClose, program: initialProgram }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    program: initialProgram || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverMsg, setServerMsg] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialProgram)
      setFormData((prev) => ({ ...prev, program: initialProgram }));
  }, [initialProgram]);

  useEffect(() => {
    const handleEscape = (e) =>
      e.key === "Escape" && isOpen && !isSubmitting && onClose?.();

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      setTimeout(() => {
        document.querySelector(".contact-form input[name='name']")?.focus();
      }, 200);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isSubmitting, onClose]);

  const validateForm = () => {
    const newErrors = {};
    const name = formData.name.trim();
    const e164 = normalizeToE164(formData.phone);
    if (!name) newErrors.name = "Пожалуйста, введите ваше имя";
    else if (name.length < 2)
      newErrors.name = "Имя должно содержать минимум 2 символа";
    const onlyDigits = e164.replace(/\D/g, "");
    if (onlyDigits.length !== 12 || !e164.startsWith("+998")) {
      newErrors.phone = "Введите корректный номер формата +998 (XX) XXX-XX-XX";
    }
    if (!formData.program.trim()) {
      newErrors.program = "Выберите программу";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMsg("");
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await sendLead({
        name: formData.name.trim(),
        phone: normalizeToE164(formData.phone),
        program: formData.program.trim(),
      });

      setIsSubmitting(false);
      setIsSubmitted(true);
      setServerMsg("Заявка принята!");

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: "", phone: "", program: "" });
        setErrors({});
        setServerMsg("");
        onClose?.();
      }, 2500);
    } catch (error) {
      console.error("Submit error:", error);
      setIsSubmitting(false);
      setServerMsg("Ошибка отправки. Попробуйте ещё раз.");
    }
  };

  const backdropVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.5, y: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", damping: 25, stiffness: 300 },
    },
    exit: { opacity: 0, scale: 0.5, y: 50, transition: { duration: 0.2 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="contact-form-overlay"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={isSubmitting ? undefined : onClose}
        >
          <motion.div
            className={`contact-form-modal ${isSubmitting ? "submitting" : ""}`}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.button
              className="close-btn"
              onClick={onClose}
              disabled={isSubmitting}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Закрыть"
            >
              <FaTimes />
            </motion.button>

            <AnimatePresence>
              {isSubmitted ? (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <motion.div
                    className="success-icon"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.6, repeat: 1 }}
                  >
                    <FaCheck />
                  </motion.div>
                  <h3>{serverMsg || "Заявка отправлена!"}</h3>
                  <p>Мы свяжемся с вами в ближайшее время</p>
                </motion.div>
              ) : (
                <motion.div
                  className="form-content"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <div className="form-header">
                    <motion.div
                      className="form-icon"
                      animate={{
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <FaPaperPlane />
                    </motion.div>
                    <h2>Оставить заявку</h2>
                    <p>Заполните форму и мы свяжемся с вами!</p>
                  </div>

                  <form onSubmit={handleSubmit} className="contact-form">
                    {/* Name */}
                    <div className="form-group">
                      <div className="input-wrapper">
                        <FaUser className="input-icon" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Ваше имя"
                          className={`form-input ${errors.name ? "error" : ""}`}
                          autoComplete="name"
                          disabled={isSubmitting}
                        />
                        {errors.name && (
                          <span className="error-message">{errors.name}</span>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="form-group">
                      <div className="input-wrapper">
                        <FaPhone className="input-icon" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={(e) => {
                            const formatted = formatUzPhoneDisplay(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              phone: formatted,
                            }));
                            if (errors.phone)
                              setErrors((prev) => ({ ...prev, phone: "" }));
                          }}
                          placeholder="+998 (XX) XXX-XX-XX"
                          className={`form-input ${errors.phone ? "error" : ""}`}
                          autoComplete="tel"
                          maxLength={22}
                          disabled={isSubmitting}
                        />
                        {errors.phone && (
                          <span className="error-message">{errors.phone}</span>
                        )}
                      </div>
                    </div>

                    {/* Program */}
                    <div className="form-group">
                      <div className="input-wrapper">
                        <FaListUl className="input-icon" />
                        <select
                          name="program"
                          value={formData.program}
                          onChange={handleInputChange}
                          className={`form-input ${errors.program ? "error" : ""}`}
                          disabled={isSubmitting}
                        >
                          <option value="">Выберите программу</option>
                          <option value="3-дневный курс">3-дневный курс</option>
                          <option value="5-дневный детокс">5-дневный детокс</option>
                        </select>
                        {errors.program && (
                          <span className="error-message">{errors.program}</span>
                        )}
                      </div>
                    </div>

                    {/* Server message */}
                    {serverMsg && !isSubmitted && (
                      <div className="server-message">{serverMsg}</div>
                    )}

                    {/* Submit */}
                    <motion.button
                      type="submit"
                      className="submit-btn"
                      disabled={isSubmitting}
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 8px 25px rgba(251, 191, 36, 0.4)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isSubmitting ? (
                        <motion.div
                          className="loading-spinner"
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        />
                      ) : (
                        <>
                          <FaPaperPlane /> Оставить заявку
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactForm;
